/**
 * Local avoidance — agent vs agent (Movement spec §3.2). A* handles static geometry;
 * it must NOT handle other agents. This is the separate layer that keeps ≤30 agents
 * from piling up, using context-steering-style separation plus chokepoint courtesy.
 *
 * - Personal space 0.45 tiles; separation ramps quadratically inside it.
 * - Chokepoint courtesy: two agents contesting a narrow tile don't shove — the one
 *   with lower HP (a call a human would also make) yields and brakes, with a small
 *   randomised hesitation so it reads as politeness, not a lock.
 * - The caller clamps the pushed velocity against the static grid (mover.push +
 *   mover.update's grid clamp), so avoidance can never shove an agent onto lava.
 */

export interface AvoidAgent {
  id: string;
  x: number; y: number;
  vx: number; vy: number;
  hp: number;
  /** True if the agent currently stands on a CHOKEPOINT tile. */
  onChoke?: boolean;
}

export interface AvoidOut { pushX: number; pushY: number; brake: number; }

export const PERSONAL_SPACE = 0.45;
/** Separation at full overlap, tiles/sec². Exported so a caller that integrates the
 * push itself can normalise against it instead of guessing the scale. */
export const SEP_FORCE = 3.0;
const AHEAD_DOT = 0.6;         // "directly ahead" cone for yielding

/**
 * Compute a separation push (and optional brake) for every agent from its neighbours.
 * O(n²) over the agent list — fine for the ≤30 the spec targets; a grid hash can
 * replace it if agent counts ever grow.
 */
export function computeAvoidance(agents: AvoidAgent[], hesitation: (id: string) => number = () => 0): Map<string, AvoidOut> {
  const out = new Map<string, AvoidOut>();
  for (const a of agents) out.set(a.id, { pushX: 0, pushY: 0, brake: 1 });

  for (let i = 0; i < agents.length; i++) {
    const a = agents[i];
    const oa = out.get(a.id)!;
    for (let j = i + 1; j < agents.length; j++) {
      const b = agents[j];
      const dx = a.x - b.x, dy = a.y - b.y;
      let d = Math.hypot(dx, dy);
      if (d >= PERSONAL_SPACE) continue;
      if (d < 1e-4) { d = 1e-4; }
      // Quadratic ramp: gentle at the edge of personal space, hard on contact.
      const t = 1 - d / PERSONAL_SPACE;
      const f = SEP_FORCE * t * t;
      const ux = dx / d, uy = dy / d;
      oa.pushX += ux * f; oa.pushY += uy * f;
      const ob = out.get(b.id)!;
      ob.pushX -= ux * f; ob.pushY -= uy * f;

      // Chokepoint / head-on courtesy: if they're near and one is heading into the
      // other, the lower-HP agent yields (brakes) instead of shoving through.
      if ((a.onChoke || b.onChoke) && d < PERSONAL_SPACE * 1.6) {
        const yielder = a.hp <= b.hp ? a : b;
        const yo = out.get(yielder.id)!;
        const toOtherX = yielder === a ? -dx : dx, toOtherY = yielder === a ? -dy : dy;
        const sp = Math.hypot(yielder.vx, yielder.vy);
        if (sp > 0.05) {
          const dot = (yielder.vx * toOtherX + yielder.vy * toOtherY) / (sp * d);
          if (dot > AHEAD_DOT) yo.brake = Math.min(yo.brake, 0.25 + 0.15 * hesitation(yielder.id));
        }
      }
    }
  }
  return out;
}
